import { Button } from "@/components/ui/button";
import { useLogout } from "@/lib/auth";
import { safeOpen } from "@/lib/safeOpen";

interface NoPartnerScreenProps {
	/** The email the user signed in with, shown so they know which account this is. */
	email?: string;
}

/**
 * Shown when a signed-in account has no partner. Rendered as a normal component
 * on a real route (deterministic) — NOT from an error boundary, which flickers.
 *
 * A plain centered layout rather than a Dialog: a Dialog portals to <body> and
 * needs its overlay to position itself, which is exactly what rendered broken
 * (content pinned top-left, no backdrop). This paints reliably on its own.
 */
export function NoPartnerScreen({ email }: NoPartnerScreenProps) {
	const logoutMutation = useLogout();

	const handleBecomePartner = () => {
		// Open the public partner page externally, then sign out of this account.
		safeOpen(`${window.location.origin}/partner`);
		logoutMutation.mutate();
	};

	// Sign out so they can log back in with a different email.
	const handleSwitchAccount = () => logoutMutation.mutate();

	return (
		<div className="flex min-h-dvh w-full flex-col items-center justify-center bg-gray-50 px-6 py-10">
			<div className="flex w-full max-w-sm flex-col items-center rounded-2xl border border-gray-100 bg-white p-8 text-center shadow-[0_8px_30px_rgba(0,0,0,0.06)]">
				{/* Emblem */}
				<div className="mb-5 flex h-16 w-16 items-center justify-center rounded-full bg-blue-50">
					<svg
						width="30"
						height="30"
						viewBox="0 0 24 24"
						fill="none"
						stroke="#0c63f8"
						strokeWidth="1.8"
						strokeLinecap="round"
						strokeLinejoin="round"
						aria-hidden="true"
					>
						<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
						<circle cx="9" cy="7" r="4" />
						<line x1="17" y1="8" x2="22" y2="13" />
						<line x1="22" y1="8" x2="17" y2="13" />
					</svg>
				</div>

				<h1 className="text-lg font-semibold text-gray-900">
					No partner account found
				</h1>

				<p className="mt-2 text-sm leading-relaxed text-gray-500">
					We couldn&apos;t find a partner account linked to
					{email ? (
						<>
							{" "}
							<span className="font-medium text-gray-700">{email}</span>.
						</>
					) : (
						" this email."
					)}{" "}
					Become a partner to get started, or sign in with a different account.
				</p>

				<div className="mt-6 flex w-full flex-col gap-2.5">
					<Button
						onClick={handleBecomePartner}
						disabled={logoutMutation.isPending}
						className="h-11 w-full"
					>
						Become a Partner
					</Button>
					<Button
						variant="outline"
						onClick={handleSwitchAccount}
						disabled={logoutMutation.isPending}
						className="h-11 w-full"
					>
						Switch account
					</Button>
				</div>
			</div>
		</div>
	);
}
